import React from 'react';
import { Calendar, Clock, Circle, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { TimelineEvent } from '../../types/invitation';
import { cn } from '../../lib/utils';
import TimelineItem from './TimelineItem';

interface TimelineSectionProps {
  id: string;
  title: string;
  type: 'overdue' | 'today' | 'tomorrow' | 'thisWeek' | 'thisMonth' | 'later' | 'wedding';
  events: TimelineEvent[];
  isExpanded: boolean;
  onToggleExpanded: (sectionId: string) => void;
  onEventEdit: (event: TimelineEvent) => void;
  onEventToggle: (eventId: string, completed: boolean) => void;
  onEventDelete: (eventId: string) => void;
}

const TimelineSection: React.FC<TimelineSectionProps> = ({
  id,
  title,
  type,
  events,
  isExpanded,
  onToggleExpanded,
  onEventEdit,
  onEventToggle,
  onEventDelete
}) => {
  const getSectionIcon = () => {
    switch (type) {
      case 'overdue':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'today':
      case 'tomorrow':
        return <Calendar className="w-5 h-5 text-wedding-pink" />;
      case 'thisWeek':
      case 'thisMonth':
        return <Calendar className="w-5 h-5 text-wedding-text-title" />;
      case 'later':
        return <Clock className="w-5 h-5 text-wedding-text-body" />;
      case 'wedding':
        return <Circle className="w-5 h-5 text-wedding-gold" />;
      default:
        return <Calendar className="w-5 h-5 text-wedding-text-title" />;
    }
  };

  const getSectionTitleColor = () => {
    switch (type) {
      case 'overdue':
        return 'text-red-500';
      case 'today':
      case 'tomorrow':
        return 'text-wedding-pink';
      case 'wedding':
        return 'text-wedding-gold';
      default:
        return 'text-wedding-text-title';
    }
  };

  if (events.length === 0) {
    return null;
  }

  return (
    <div className="timeline-section relative mb-8">
      {/* 時間段標題 */}
      <div 
        className="flex items-center justify-between cursor-pointer hover:bg-wedding-pink/5 rounded-lg p-2 -mx-2 transition-colors"
        onClick={() => onToggleExpanded(id)}
      >
        <div className="flex items-center gap-3">
          {getSectionIcon()}
          <h2 className={cn(
            "text-xl font-semibold",
            getSectionTitleColor()
          )}>
            {title}
          </h2>
          <span className="text-sm bg-wedding-pink/10 text-wedding-pink px-2 py-1 rounded-full">
            {events.length}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {/* 快速統計 */}
          <div className="hidden md:flex items-center gap-2 text-sm text-wedding-text-body">
            <span>
              已完成 {events.filter(e => e.is_completed).length}/{events.length}
            </span>
          </div>
          
          {/* 展開/收縮圖標 */}
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-wedding-text-body" />
          ) : (
            <ChevronRight className="w-4 h-4 text-wedding-text-body" />
          )}
        </div>
      </div>

      {/* 事件列表 */}
      {isExpanded && (
        <div className="mt-4 space-y-0 relative">
          {/* 時間軸背景線 */}
          <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-wedding-pink/30 to-wedding-pink/10"></div>
          
          {events.map((event) => (
            <TimelineItem
              key={event.id}
              event={event}
              onEdit={onEventEdit}
              onToggle={onEventToggle}
              onDelete={onEventDelete}
            />
          ))}
        </div>
      )}

      {/* 簡化視圖（收縮時顯示） */}
      {!isExpanded && events.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {events.slice(0, 3).map((event) => (
            <div
              key={event.id}
              className={cn(
                "text-xs px-2 py-1 rounded-full cursor-pointer hover:shadow-sm transition-all",
                event.is_completed 
                  ? "bg-green-100 text-green-700 line-through" 
                  : "bg-wedding-pink/10 text-wedding-pink"
              )}
              onClick={() => onEventEdit(event)}
            >
              {event.title}
            </div>
          ))}
          {events.length > 3 && (
            <div className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
              +{events.length - 3} 更多
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimelineSection;